/**
 * The catalog of every declared setting (I-2510): the sections each app
 * declares (`ConnectAppModule.settingsSections`) plus Connect's own, with
 * their texts translated by the table of the app that declares them. The
 * shell builds it — only the shell loads the apps' modules and knows their
 * tables — and provides it app-wide (`connectSettingsCatalogKey`), so every
 * settings list reads the same one: the shell's settings page, the look
 * designer and the admin app's panels.
 */
import {
  computed,
  inject,
  shallowRef,
  type Component,
  type InjectionKey,
  type Ref,
} from "vue";
import {
  connectSettingsFieldId,
  connectSettingsPath,
  type ConnectSettingsControl,
  type ConnectSettingsDocumentDescriber,
  type ConnectSettingsDocumentLine,
  type ConnectSettingsField,
  type ConnectSettingsPage,
  type ConnectSettingsSection,
  type ConnectSettingsValue,
} from "../config/settingsDeclaration";
import type { JsonObject } from "../config/mergePatch";

/** A keybinding check that names its reason by i18n key, as one that names it in words. */
function translatedProblem(
  problem: (combo: string) => string | null,
  t: (key: string) => string,
): (combo: string) => string | null {
  return (combo) => {
    const key = problem(combo);
    return key ? t(key) : null;
  };
}

/** A declared field with its texts translated. */
export interface ConnectSettingsFieldView {
  /** `table:zebra` (`connectSettingsFieldId`). */
  id: string;
  key: string;
  path: readonly string[];
  control: ConnectSettingsControl;
  folderPermission?: "read" | "write";
  options: readonly { value: string | number | null; label: string }[];
  values?: { on: ConnectSettingsValue; off: ConnectSettingsValue };
  min?: number;
  max?: number;
  step?: number;
  /** The unit after a number, in the reader's language (`unitKey`) or as declared. */
  unit?: string;
  maxLength?: number;
  default: ConnectSettingsValue | null;
  label: string;
  description: string;
  keywords: readonly string[];
  advanced: boolean;
  adminOnly: boolean;
  /** Only the installation's layer lists it (`ConnectSettingsField.installationOnly`). */
  installationOnly?: boolean;
  /** A tenant or company may lock it (`ConnectSettingsField.lockable`). */
  lockable: boolean;
  group?: string;
  /** `ConnectSettingsField.distinctGroup`, when the field sets one. */
  distinctGroup?: string;
  /** `ConnectSettingsField.keybindingProblem` with the reason already translated. */
  keybindingProblem?: (combo: string) => string | null;
  link?: { label: string; to: string };
}

/** A declared section with its texts translated. */
export interface ConnectSettingsSectionView {
  id: string;
  /** The app that declares it (`mail`); unset for Connect's own. */
  app?: string;
  label: string;
  description?: string;
  /** The app's hub group it is listed under (an i18n key of the app's table). */
  sectionKey?: string;
  keywords: readonly string[];
  groups: readonly { id: string; label: string }[];
  link?: { label: string; to: string };
  /** Per-object overrides of the section's fields (see `ConnectSettingsSection.objectOverrides`). */
  objectOverrides?: { key: string; field: string; countLabel: string };
  fields: readonly ConnectSettingsFieldView[];
}

/** A declared page with its texts translated. */
export interface ConnectSettingsPageView {
  /** The config key of its arrangement (`erp.screen.sales_quotes_show`). */
  key: string;
  /** The app that declares it (`erp`). */
  app?: string;
  label: string;
  /** The area it is listed under. */
  group?: string;
  grids: readonly string[];
  to?: string;
}

/** Where declared sections come from: an app (or the shell) and its translator. */
export interface ConnectSettingsDeclarationSource {
  /** The declaring app (`mail`); unset for Connect's own. */
  app?: string;
  sections: readonly ConnectSettingsSection[];
  /** The pages whose arrangement a design can carry (`ConnectAppModule.settingsPages`). */
  pages?: readonly ConnectSettingsPage[];
  /** Describes the app's stored documents (`ConnectAppModule.describeSettingsDocument`). */
  describe?: ConnectSettingsDocumentDescriber;
  /** The app's page editor (`ConnectAppModule.settingsPageEditor`). */
  pageEditor?: () => Promise<Component | { default: Component }>;
  /** `t` over the declaring app's own table. */
  t: (key: string, params?: Record<string, unknown>) => string;
}

/** The unit after a number: a translated `unitKey`, else the plain `unit`. */
function unitOf(
  field: ConnectSettingsField,
  t: ConnectSettingsDeclarationSource["t"],
): { unit?: string } {
  const unit = field.unitKey ? t(field.unitKey) : field.unit;
  return unit ? { unit } : {};
}

/**
 * The sections of every source with their texts translated, in the sources'
 * order. A section id declared twice keeps its first declaration (the guard
 * of each app's declaration catches the clash within an app). Preview fields
 * (`ConnectSettingsField.preview`) are left out by default; `previews: 'only'`
 * keeps nothing but them — the look designer's „In Entwicklung“.
 */
export function resolveConnectSettingsSections(
  sources: readonly ConnectSettingsDeclarationSource[],
  options: { previews?: boolean | "only" } = {},
): ConnectSettingsSectionView[] {
  const seen = new Set<string>();
  const out: ConnectSettingsSectionView[] = [];
  for (const source of sources) {
    const t = source.t;
    for (const section of source.sections) {
      if (seen.has(section.id)) continue;
      seen.add(section.id);
      // A preview field shows only where previews are asked for; a section
      // left without fields is left out with them.
      const fields =
        options.previews === true
          ? section.fields
          : section.fields.filter(
              (field) => (field.preview === true) === (options.previews === "only"),
            );
      if (!fields.length) continue;
      out.push({
        id: section.id,
        ...(source.app ? { app: source.app } : {}),
        label: t(section.labelKey),
        ...(section.descriptionKey
          ? { description: t(section.descriptionKey) }
          : {}),
        ...(section.sectionKey ? { sectionKey: section.sectionKey } : {}),
        keywords: [...(section.keywords ?? [])],
        groups: (section.groups ?? []).map((group) => ({
          id: group.id,
          label: t(group.labelKey),
        })),
        ...(section.link
          ? { link: { label: t(section.link.labelKey), to: section.link.to } }
          : {}),
        ...(section.objectOverrides
          ? {
              objectOverrides: {
                key: section.objectOverrides.key,
                field: section.objectOverrides.field,
                countLabel: t(section.objectOverrides.countLabelKey),
              },
            }
          : {}),
        fields: fields.map((field) => ({
          id: connectSettingsFieldId(field),
          key: field.key,
          path: connectSettingsPath(field),
          control: field.control,
          ...(field.folderPermission ? { folderPermission: field.folderPermission } : {}),
          options: (field.options ?? []).map((option) => ({
            value: option.value,
            label: option.label?.trim() || t(option.labelKey ?? "", option.labelParams),
          })),
          ...(field.values ? { values: { ...field.values } } : {}),
          ...(field.min !== undefined ? { min: field.min } : {}),
          ...(field.max !== undefined ? { max: field.max } : {}),
          ...(field.step !== undefined ? { step: field.step } : {}),
          ...unitOf(field, t),
          ...(field.maxLength ? { maxLength: field.maxLength } : {}),
          default: field.default,
          label: t(field.labelKey),
          description: t(field.descriptionKey),
          keywords: [...(field.keywords ?? [])],
          advanced: field.advanced === true,
          adminOnly: field.adminOnly === true,
          ...(field.installationOnly ? { installationOnly: true } : {}),
          lockable: field.lockable !== false,
          ...(field.group ? { group: field.group } : {}),
          ...(field.distinctGroup ? { distinctGroup: field.distinctGroup } : {}),
          ...(field.keybindingProblem
            ? { keybindingProblem: translatedProblem(field.keybindingProblem, t) }
            : {}),
          ...(field.link
            ? { link: { label: t(field.link.labelKey), to: field.link.to } }
            : {}),
        })),
      });
    }
  }
  return out;
}

/**
 * The pages of every source with their texts translated, in the sources'
 * order. A key declared twice keeps its first declaration.
 */
export function resolveConnectSettingsPages(
  sources: readonly ConnectSettingsDeclarationSource[],
): ConnectSettingsPageView[] {
  const seen = new Set<string>();
  const out: ConnectSettingsPageView[] = [];
  for (const source of sources) {
    for (const page of source.pages ?? []) {
      if (seen.has(page.key)) continue;
      seen.add(page.key);
      out.push({
        key: page.key,
        ...(source.app ? { app: source.app } : {}),
        label: source.t(page.labelKey),
        ...(page.groupKey ? { group: source.t(page.groupKey) } : {}),
        grids: [...new Set(page.grids ?? [])],
        ...(page.to ? { to: page.to } : {}),
      });
    }
  }
  return out;
}

/**
 * A stored document of `key` as the app that owns the key describes it (the
 * app is the key's first segment); null when no app describes it.
 */
export function describeConnectSettingsDocument(
  sources: readonly ConnectSettingsDeclarationSource[],
  key: string,
  doc: JsonObject,
): ConnectSettingsDocumentLine[] | null {
  const app = key.split(".")[0];
  const source = sources.find((item) => item.app === app && item.describe);
  if (!source?.describe) return null;
  try {
    return source.describe(key, doc, source.t);
  } catch (error) {
    console.warn("[settings] could not describe", key, error);
    return null;
  }
}

/** Every declared setting, as the shell collected it. */
export interface ConnectSettingsCatalog {
  /** The sections of Connect and of every app that takes part, texts translated. */
  readonly sections: Readonly<Ref<readonly ConnectSettingsSectionView[]>>;
  /**
   * The preview fields alone (`ConnectSettingsField.preview`), in their
   * sections — not in `sections`; the look designer lists them under
   * „In Entwicklung“.
   */
  readonly previewSections: Readonly<Ref<readonly ConnectSettingsSectionView[]>>;
  /** The pages the apps declare (`ConnectAppModule.settingsPages`) — the look designer's „Seiten". */
  readonly pages: Readonly<Ref<readonly ConnectSettingsPageView[]>>;
  /** A stored document as the app that owns its key describes it; null when none does. */
  describeDocument(key: string, doc: JsonObject): ConnectSettingsDocumentLine[] | null;
  /** The loader of an app's page editor (`ConnectAppModule.settingsPageEditor`); null when it has none. */
  pageEditor(app: string): (() => Promise<Component | { default: Component }>) | null;
  /** The apps' declarations are on their way. */
  readonly loading: Readonly<Ref<boolean>>;
  /** Collects the apps' declarations (once; later calls wait for that one). */
  load(): Promise<void>;
}

export const connectSettingsCatalogKey: InjectionKey<ConnectSettingsCatalog> =
  Symbol("ConnectSettingsCatalog");

/**
 * A catalog over a loader of declaration sources — the shell's: Connect's
 * own sections at once (`initial`), the apps' once `load` collected them. The
 * texts follow the language: they are translated when read.
 */
export function createConnectSettingsCatalog(
  loader: () => Promise<readonly ConnectSettingsDeclarationSource[]>,
  initial: readonly ConnectSettingsDeclarationSource[] = [],
): ConnectSettingsCatalog {
  const sources = shallowRef<readonly ConnectSettingsDeclarationSource[]>(initial);
  const loading = shallowRef(false);
  let running: Promise<void> | null = null;
  return {
    sections: computed(() => resolveConnectSettingsSections(sources.value)),
    previewSections: computed(() =>
      resolveConnectSettingsSections(sources.value, { previews: "only" }),
    ),
    pages: computed(() => resolveConnectSettingsPages(sources.value)),
    describeDocument: (key, doc) => describeConnectSettingsDocument(sources.value, key, doc),
    pageEditor: (app) => sources.value.find((source) => source.app === app)?.pageEditor ?? null,
    loading,
    load() {
      if (!running) {
        loading.value = true;
        running = loader()
          .then((loaded) => {
            sources.value = loaded;
          })
          .catch((error: unknown) => {
            console.warn("[settings] could not collect the declared settings", error);
            running = null;
          })
          .finally(() => {
            loading.value = false;
          });
      }
      return running ?? Promise.resolve();
    },
  };
}

const emptyCatalog: ConnectSettingsCatalog = {
  sections: shallowRef([]),
  previewSections: shallowRef([]),
  pages: shallowRef([]),
  describeDocument: () => null,
  pageEditor: () => null,
  loading: shallowRef(false),
  load: () => Promise.resolve(),
};

/** The catalog the shell provides; outside the shell an empty one. */
export function useConnectSettingsCatalog(): ConnectSettingsCatalog {
  return inject(connectSettingsCatalogKey, emptyCatalog);
}
